import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ApprovalHistoryTimeline } from './approval-history-timeline';
import { useCompany } from '@/contexts/CompanyContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  DollarSign,
  FileText,
  TrendingUp,
  Download,
  File,
  FileSpreadsheet,
  Image,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import type { PurchaseRequest, PurchaseRequestStatus, PurchaseRequestSummary, Document } from '@/types/purchase-request';
import type { Employee } from '@/types/index';
import { fetchPurchaseRequests } from '@/lib/mock-purchase-requests';
import { fetchFinOpsPurchaseRequests, fetchFinOpsPurchaseRequestDetails, isFinOpsIntegrationAvailable } from '@/lib/finops-api';
import { getDataMode } from '@/lib/data-service';
import { format } from 'date-fns';
import { PurchaseRequestFullModal } from './purchase-request-full-modal';
import { Link2Off } from 'lucide-react';

interface PurchasesCommitmentsTabProps {
  projectId: number;
  projectCode?: string;
  employees?: Employee[];
}

type SortField = 'requestId' | 'description' | 'status' | 'amount' | 'supplier' | 'date';
type SortDirection = 'asc' | 'desc';

const statusConfig: Record<string, {
  variant: 'success' | 'warning' | 'destructive' | 'default';
  accent: 'success' | 'warning' | 'destructive' | 'info';
  icon: typeof CheckCircle2;
  color: string;
}> = {
  Approved: {
    variant: 'success',
    accent: 'success',
    icon: CheckCircle2,
    color: 'text-green-600',
  },
  Pending: {
    variant: 'warning',
    accent: 'warning',
    icon: Clock,
    color: 'text-orange-600',
  },
  Rejected: {
    variant: 'destructive',
    accent: 'destructive',
    icon: XCircle,
    color: 'text-red-600',
  },
  Posted: {
    variant: 'default',
    accent: 'info',
    icon: CheckCircle2,
    color: 'text-blue-600',
  },
  Redirected: {
    variant: 'warning',
    accent: 'warning',
    icon: AlertCircle,
    color: 'text-orange-600',
  },
  Reopened: {
    variant: 'warning',
    accent: 'warning',
    icon: AlertCircle,
    color: 'text-yellow-600',
  },
  Cancelled: {
    variant: 'destructive',
    accent: 'destructive',
    icon: XCircle,
    color: 'text-gray-600',
  },
  Draft: {
    variant: 'default',
    accent: 'info',
    icon: FileText,
    color: 'text-gray-600',
  },
};

// Helper function to get status config with fallback for unknown statuses
const getStatusConfig = (status: string) => {
  return statusConfig[status] || {
    variant: 'default' as const,
    accent: 'info' as const,
    icon: AlertCircle,
    color: 'text-gray-600',
  };
};

const getDocumentIcon = (type: Document['type']) => {
  switch (type) {
    case 'pdf':
      return FileText;
    case 'image':
      return Image;
    case 'spreadsheet':
      return FileSpreadsheet;
    case 'document':
      return FileText;
    default:
      return File;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

// Helper function to calculate PR age in days
const calculatePRAge = (request: PurchaseRequest): number | null => {
  // Only calculate age if PR is not in a final state
  const finalStatuses = ['Approved', 'Posted', 'Rejected', 'Cancelled'];
  if (finalStatuses.includes(request.status)) {
    return null;
  }
  
  const prDate = new Date(request.date);
  const today = new Date();
  const diffTime = Math.abs(today.getTime() - prDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Helper function to get current approval stage from approval history
const getCurrentApprovalStage = (request: PurchaseRequest): string => {
  if (!request.approvalHistory || request.approvalHistory.length === 0) {
    return '';
  }
  
  // Get the most recent entry
  const sortedHistory = [...request.approvalHistory].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  
  const latestEntry = sortedHistory[0];
  return latestEntry.stage || '';
};

export function PurchasesCommitmentsTab({ projectId, projectCode, employees = [] }: PurchasesCommitmentsTabProps) {
  const { currentCompany } = useCompany();
  const [requests, setRequests] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PurchaseRequestStatus | 'all'>('all');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showDocuments, setShowDocuments] = useState(false);
  const [fullModalOpen, setFullModalOpen] = useState(false);
  const [approvalHistorySheetOpen, setApprovalHistorySheetOpen] = useState(false);
  const [approvalHistoryRequest, setApprovalHistoryRequest] = useState<PurchaseRequest | null>(null);
  const [approvalHistoryExpanded, setApprovalHistoryExpanded] = useState(false);
  const [isFinOpsMode, setIsFinOpsMode] = useState(false);
  const [finOpsNotConfigured, setFinOpsNotConfigured] = useState(false);

  // Resolve an ExternalUserId (e.g. "AM1") to the employee's display name
  const getEmployeeNameByExternalUserId = (externalUserId: string): string => {
    if (!externalUserId) return 'Unknown Employee';
    const employee = employees.find(
      (e) => e.externalUserId?.toLowerCase() === externalUserId.toLowerCase()
    );
    if (!employee && employees.length > 0) {
      const employeesWithExtId = employees.filter((e) => e.externalUserId);
      if (employeesWithExtId.length === 0) {
        console.warn(
          `[PurchasesTab] No employees have externalUserId set. Run the "Sync Financials User IDs" action for this company to populate them. Tried to resolve: "${externalUserId}"`
        );
      }
    }
    return employee?.name || externalUserId;
  };

  // Fetch data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setFinOpsNotConfigured(false);
        
        const dataMode = getDataMode();
        const companyId = currentCompany?.id;
        const companyInstanceId = currentCompany?.instanceId;
        
        
        // In live mode, check if company has an instanceId configured (FinOps integration)
        // In demo mode, check localStorage for integration configuration
        const finOpsAvailable = dataMode === 'live' 
          ? !!companyInstanceId 
          : isFinOpsIntegrationAvailable(companyId);
        
        
        // In live mode, try to use FinOps API if integration is available and project code exists
        if (dataMode === 'live' && projectCode) {
          if (finOpsAvailable) {
            setIsFinOpsMode(true);
            try {
              const finOpsData = await fetchFinOpsPurchaseRequests(projectCode, projectId, companyId, companyInstanceId);
              if (finOpsData) {
                setRequests(finOpsData);
              } else {
                setRequests([]);
              }
              return;
            } catch (finOpsError) {
              // FinOps API unavailable (e.g. 401 when running locally without preprod auth)
              // Fall back gracefully to empty state instead of showing an error
              console.warn('[PurchasesCommitmentsTab] FinOps API unavailable, showing empty state:', finOpsError);
              setRequests([]);
              return;
            }
          } else {
            // FinOps integration not configured - show message
            setFinOpsNotConfigured(true);
            setRequests([]);
            return;
          }
        }
        
        if (dataMode === 'live') {
          setRequests([]);
          return;
        }
        
        // Fallback to mock data (demo mode only)
        setIsFinOpsMode(false);
        const data = await fetchPurchaseRequests(projectId);
        setRequests(data);
      } catch (err) {
        setError('Unable to load purchase data. Please try again later.');
        console.error('Error fetching purchase requests:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, projectCode, currentCompany?.id, currentCompany?.instanceId]);

  // Get unique suppliers for filter (filter out empty values to avoid Radix Select crash)
  const suppliers = useMemo(() => {
    const uniqueSuppliers = Array.from(new Set(requests.map((r) => r.supplier).filter(Boolean))).sort();
    return uniqueSuppliers;
  }, [requests]);

  // Calculate summary
  const summary: PurchaseRequestSummary = useMemo(() => {
    const approvedRequests = requests.filter((r) => r.status === 'Approved');
    const totalCommittedCost = approvedRequests.reduce((sum, r) => sum + r.amount, 0);
    const totalApprovedRequests = approvedRequests.length;
    const totalPendingRequests = requests.filter((r) => r.status === 'Pending').length;
    const totalRejectedRequests = requests.filter((r) => r.status === 'Rejected').length;
    const averageRequestAmount = requests.length > 0 ? requests.reduce((sum, r) => sum + r.amount, 0) / requests.length : 0;

    return {
      totalCommittedCost,
      totalApprovedRequests,
      totalPendingRequests,
      totalRejectedRequests,
      averageRequestAmount,
    };
  }, [requests]);

  // Filter and sort data
  const filteredAndSortedRequests = useMemo(() => {
    let filtered = [...requests];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.requestId.toLowerCase().includes(query) ||
          r.description.toLowerCase().includes(query) ||
          r.supplier.toLowerCase().includes(query) ||
          getEmployeeNameByExternalUserId(r.supplier).toLowerCase().includes(query) ||
          r.category.toLowerCase().includes(query) ||
          r.requestedBy.toLowerCase().includes(query) ||
          getEmployeeNameByExternalUserId(r.requestedBy).toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    // Apply supplier filter
    if (supplierFilter !== 'all') {
      filtered = filtered.filter((r) => r.supplier === supplierFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: string | number = a[sortField];
      let bValue: string | number = b[sortField];

      if (sortField === 'date') {
        aValue = new Date(a.date).getTime();
        bValue = new Date(b.date).getTime();
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return filtered;
  }, [requests, searchQuery, statusFilter, supplierFilter, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedRequests.length / pageSize);
  const paginatedRequests = filteredAndSortedRequests.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, supplierFilter, sortField, sortDirection, pageSize]);



  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-4 w-4 inline" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1 h-4 w-4 inline" />
    ) : (
      <ArrowDown className="ml-1 h-4 w-4 inline" />
    );
  };

  const formatCurrency = (amount: number, currency: string = 'GBP') => {
    const currencySymbols: Record<string, string> = {
      GBP: '£',
      EUR: '€',
      USD: '$',
    };

    const symbol = currencySymbols[currency] || currency;
    
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    }).format(amount) + ` ${currency}`;
  };

  const handleRetry = async () => {
    setError(null);
    setLoading(true);
    setFinOpsNotConfigured(false);
    
    try {
      const dataMode = getDataMode();
      const companyId = currentCompany?.id;
      const companyInstanceId = currentCompany?.instanceId;
      
      // In live mode, check if company has an instanceId configured (FinOps integration)
      // In demo mode, check localStorage for integration configuration
      const finOpsAvailable = dataMode === 'live' 
        ? !!companyInstanceId 
        : isFinOpsIntegrationAvailable(companyId);
      
      if (dataMode === 'live' && projectCode) {
        if (finOpsAvailable) {
          setIsFinOpsMode(true);
          const finOpsData = await fetchFinOpsPurchaseRequests(projectCode, projectId, companyId, companyInstanceId);
          if (finOpsData) {
            setRequests(finOpsData);
          } else {
            setRequests([]);
          }
          setLoading(false);
          return;
        } else {
          setFinOpsNotConfigured(true);
          setRequests([]);
          setLoading(false);
          return;
        }
      }
      
      if (dataMode === 'live') {
        setRequests([]);
        setLoading(false);
        return;
      }
      
      // Fallback to mock data (demo mode only)
      setIsFinOpsMode(false);
      const data = await fetchPurchaseRequests(projectId);
      setRequests(data);
      setLoading(false);
    } catch (err) {
      // FinOps API unavailable - fall back gracefully to empty state
      console.warn('[PurchasesCommitmentsTab] FinOps API unavailable on retry:', err);
      setRequests([]);
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-24 mb-2" />
                <Skeleton className="h-3 w-40" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-destructive/10 p-3">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Error Loading Data</h3>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
              <Button onClick={handleRetry}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // FinOps not configured state
  if (finOpsNotConfigured) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <Link2Off className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">FinOps Integration Not Configured</h3>
                <p className="text-sm text-muted-foreground">
                  The FinOps integration has not been set up for this company. Please configure the integration in Settings to view purchase requests.
                </p>
              </div>
              <Button variant="outline" onClick={handleRetry}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Empty state
  if (requests.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <FileText className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">No Purchase Requests</h3>
                <p className="text-sm text-muted-foreground">
                  There are no purchase requests associated with this project yet.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Committed Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatCurrency(summary.totalCommittedCost)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              From {summary.totalApprovedRequests} approved requests
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Approved Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <div className="text-2xl font-bold">{summary.totalApprovedRequests}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {((summary.totalApprovedRequests / requests.length) * 100).toFixed(0)}% of total requests
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              <div className="text-2xl font-bold">{summary.totalPendingRequests}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Awaiting approval
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Average Request
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatCurrency(summary.averageRequestAmount)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all requests
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card>
        <CardHeader>
          <CardTitle>Purchase Requests</CardTitle>
          <CardDescription>
            View purchase requests{currentCompany ? ` from ${currentCompany.name}` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search requests..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as PurchaseRequestStatus | 'all')}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Filter by supplier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Suppliers</SelectItem>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier} value={supplier}>
                      {getEmployeeNameByExternalUserId(supplier)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground mb-4">
            Showing {paginatedRequests.length} of {filteredAndSortedRequests.length} requests
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('requestId')}>
                    Request ID {getSortIcon('requestId')}
                  </TableHead>
                  <TableHead>
                    Order Number
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('description')}>
                    Description {getSortIcon('description')}
                  </TableHead>
                  <TableHead>
                    Category
                  </TableHead>
                  <TableHead>
                    Requested By
                  </TableHead>
                  <TableHead>
                    Current Stage
                  </TableHead>
                  <TableHead>
                    Cost Centre
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('status')}>
                    Status {getSortIcon('status')}
                  </TableHead>
                  <TableHead className="cursor-pointer text-right" onClick={() => handleSort('amount')}>
                    Amount {getSortIcon('amount')}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('supplier')}>
                    Supplier {getSortIcon('supplier')}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('date')}>
                    Date {getSortIcon('date')}
                  </TableHead>
                  <TableHead>
                    Days Open
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                      No requests match your filters
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRequests.map((request) => {
                    const config = getStatusConfig(request.status);
                    const StatusIcon = config.icon;
                    return (
                      <TableRow
                        key={request.id}
                        className="hover:bg-muted/50"
                      >
                        <TableCell 
                          className="font-medium text-primary hover:underline cursor-pointer"
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowDocuments(false);
                          }}
                        >
                          {request.requestId}
                        </TableCell>
                        <TableCell>
                          {request.poNumber && request.poNumber.length > 0 ? (
                            request.poNumber.length === 1 ? (
                              <span className="text-sm">{request.poNumber[0]}</span>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm truncate">{request.poNumber[0]}</span>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Badge 
                                      variant="secondary" 
                                      className="cursor-pointer hover:bg-secondary/80 text-xs px-1.5 py-0 h-5 shrink-0"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      +{request.poNumber.length - 1} more
                                    </Badge>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-3" align="start">
                                    <div className="space-y-1">
                                      <p className="text-xs font-medium text-muted-foreground mb-2">Purchase Orders</p>
                                      {request.poNumber.map((po, index) => (
                                        <div key={index} className="text-sm font-mono">
                                          {po}
                                        </div>
                                      ))}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              </div>
                            )
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[300px] truncate">{request.description}</TableCell>
                        <TableCell className="text-sm">{request.category}</TableCell>
                        <TableCell className="text-sm">{getEmployeeNameByExternalUserId(request.requestedBy)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {getCurrentApprovalStage(request) || '—'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {request.costCentreCode ? (
                            <div className="flex flex-col">
                              <span className="font-medium">{request.costCentreCode}</span>
                              {request.costCentreName && (
                                <span className="text-xs text-muted-foreground">{request.costCentreName}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={config.variant}
                            accent={config.accent}
                            className="gap-1 cursor-pointer hover:opacity-80"
                            onClick={(e) => {
                              e.stopPropagation();
                              setApprovalHistoryRequest(request);
                              setApprovalHistorySheetOpen(true);
                            }}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {request.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(request.amount, request.currency)}
                        </TableCell>
                        <TableCell>{getEmployeeNameByExternalUserId(request.supplier)}</TableCell>
                        <TableCell>{format(new Date(request.date), 'dd MMM yyyy')}</TableCell>
                        <TableCell className="text-sm">
                          {(() => {
                            const age = calculatePRAge(request);
                            if (age === null) {
                              return <span className="text-muted-foreground">—</span>;
                            }
                            const isOld = age > 30;
                            return (
                              <Badge 
                                variant={isOld ? 'destructive' : 'default'}
                                accent={isOld ? 'destructive' : 'info'}
                                className="text-xs"
                              >
                                {age} {age === 1 ? 'day' : 'days'}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {filteredAndSortedRequests.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet open={!!selectedRequest} onOpenChange={(open) => {
        if (!open) {
          setSelectedRequest(null);
          setShowDocuments(false);
        }
      }}>
        <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
          {selectedRequest && (
            <>
              <SheetHeader className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      <SheetTitle>{selectedRequest.requestId}</SheetTitle>
                    </div>
                    <SheetDescription>Purchase Request Details</SheetDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedRequest(null);
                      setFullModalOpen(true);
                      // Re-select the request after a brief delay to allow sheet to close
                      setTimeout(() => {
                        setSelectedRequest(selectedRequest);
                      }, 100);
                    }}
                    title="Expand to full view"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                </div>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                {/* Status Badge */}
                <div>
                  {(() => {
                    const config = getStatusConfig(selectedRequest.status);
                    const StatusIcon = config.icon;
                    return (
                      <Badge
                        variant={config.variant}
                        accent={config.accent}
                        className="gap-1"
                      >
                        <StatusIcon className="h-3 w-3" />
                        {selectedRequest.status}
                      </Badge>
                    );
                  })()}
                </div>

                {/* Key Information */}
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Description</label>
                    <p className="mt-1 text-sm">{selectedRequest.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Amount</label>
                      <p className="mt-1 text-lg font-semibold">
                        {formatCurrency(selectedRequest.amount, selectedRequest.currency)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Currency</label>
                      <p className="mt-1 text-sm font-medium">{selectedRequest.currency}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Date</label>
                      <p className="mt-1 text-sm">{format(new Date(selectedRequest.date), 'dd MMMM yyyy')}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Category</label>
                      <p className="mt-1 text-sm">{selectedRequest.category}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Supplier</label>
                    <p className="mt-1 text-sm">{getEmployeeNameByExternalUserId(selectedRequest.supplier)}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Requested By</label>
                    <p className="mt-1 text-sm">
                      {getEmployeeNameByExternalUserId(selectedRequest.requestedBy)} on {format(new Date(selectedRequest.date), 'dd MMM yyyy')}
                    </p>
                  </div>

                  {selectedRequest.approvedBy && selectedRequest.approvedDate && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Approved By</label>
                      <p className="mt-1 text-sm">
                        {getEmployeeNameByExternalUserId(selectedRequest.approvedBy)} on {format(new Date(selectedRequest.approvedDate), 'dd MMM yyyy')}
                      </p>
                    </div>
                  )}

                  {selectedRequest.notes && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Notes</label>
                      <p className="mt-1 text-sm">{selectedRequest.notes}</p>
                    </div>
                  )}
                </div>

                {/* Documents Section */}
                <div className="pt-4 border-t">
                  <Button
                    variant="outline"
                    className="w-full justify-between"
                    onClick={() => setShowDocuments(!showDocuments)}
                    disabled={!selectedRequest.documents || selectedRequest.documents.length === 0}
                  >
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {selectedRequest.documents && selectedRequest.documents.length > 0
                        ? `View Documents (${selectedRequest.documents.length})`
                        : 'No documents available'}
                    </span>
                    {selectedRequest.documents && selectedRequest.documents.length > 0 && (
                      showDocuments ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>

                  {showDocuments && selectedRequest.documents && selectedRequest.documents.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {selectedRequest.documents.map((document) => {
                        const DocumentIcon = getDocumentIcon(document.type);
                        return (
                          <div
                            key={document.id}
                            className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex-shrink-0">
                                <DocumentIcon className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{document.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatFileSize(document.size)} • {format(new Date(document.uploadDate), 'dd MMM yyyy')}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="flex-shrink-0"
                              asChild
                            >
                              <a
                                href={document.url}
                                download={document.name}
                                onClick={(e) => {
                                  e.preventDefault();
                                  // Mock download - in real app this would trigger actual download
                                  console.log('Downloading:', document.name);
                                  alert(`Mock download: ${document.name}`);
                                }}
                              >
                                <Download className="h-4 w-4" />
                              </a>
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="pt-4 border-t">
                  <Button className="w-full" variant="outline" asChild>
                    <a
                      href={`#/financials/purchase-requests/${selectedRequest.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View in Access Financials Operations
                    </a>
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Full Modal */}
      <PurchaseRequestFullModal
        purchaseRequest={selectedRequest}
        allPurchaseRequests={filteredAndSortedRequests}
        open={fullModalOpen}
        onOpenChange={setFullModalOpen}
        onPurchaseRequestChange={(pr) => {
          setSelectedRequest(pr);
        }}
        onMinimize={() => {
          setFullModalOpen(false);
          // Brief delay to allow modal to close before opening sheet
          setTimeout(() => {
            setSelectedRequest(selectedRequest);
          }, 100);
        }}
        projectCode={projectCode}
        instanceId={currentCompany?.instanceId}
        onFetchLineDetails={isFinOpsMode ? fetchFinOpsPurchaseRequestDetails : undefined}
        resolveEmployeeName={getEmployeeNameByExternalUserId}
      />

      {/* Approval History Sheet */}
      {approvalHistoryExpanded ? (
        <Dialog open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>
                    Approval History for {approvalHistoryRequest?.requestId}
                  </DialogTitle>
                  <DialogDescription>
                    View the complete approval timeline for this purchase request
                  </DialogDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setApprovalHistoryExpanded(false)}
                  title="Return to sheet view"
                >
                  <Minimize2 className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {approvalHistoryRequest?.approvalHistory && (
                <ApprovalHistoryTimeline 
                  approvalHistory={approvalHistoryRequest.approvalHistory}
                  requestId={approvalHistoryRequest.requestId}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
          <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
            <SheetHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle>
                    Approval History for {approvalHistoryRequest?.requestId}
                  </SheetTitle>
                  <SheetDescription>
                    View the complete approval timeline for this purchase request
                  </SheetDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setApprovalHistoryExpanded(true)}
                  title="Expand to full view"
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {approvalHistoryRequest?.approvalHistory && (
                <ApprovalHistoryTimeline 
                  approvalHistory={approvalHistoryRequest.approvalHistory}
                  requestId={approvalHistoryRequest.requestId}
                />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
